import { isArray } from "@apollo/client/utilities";
import { ArrowDropDown, ArrowRight, ExpandLess, ExpandMore } from "@mui/icons-material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Autocomplete, Box, Checkbox, IconButton, Switch, TextField } from "@mui/material";
import {
	GridRowSelectionModel,
	GridValidRowModel,
	GridCellParams,
	GridCallbackDetails,
	GridRowId,
	GridRenderCellParams,
	GridColDef,
	DataGridPremium,
	GridRowParams,
	useGridApiRef,
	GridSortModel,
} from "@mui/x-data-grid-premium";
import { deDE as dataGridDeDE } from "@mui/x-data-grid-premium/locales";
import {
	ApprovalTimeEntryStatus,
	CommercialDocumentSummary,
	Contact,
	ContactType,
	DocumentStatus,
	DocumentType,
	TimeBalanceHistoryEntry,
	useGetAllFederalStatesLazyQuery,
} from "@repo/server-api";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { data, useLocation } from "react-router-dom";

import usePersistentState from "../../../store/usePersistentState";
import LoadingOverlay from "../LoadingOverlay";
import NoData from "../NoData";
import { DataSource } from "./DataSource";
import DateField from "../fields/DateField";
import Dropdown from "../sections/Dropdown";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface PaginationModel {
	page: number;
	pageSize: number;
}

interface Localization {
	searchPlaceholder?: string;
	noDataMessage?: string;
	displayedRows?: (from: number, to: number, count: number) => string;
}

interface ListProps<T extends GridValidRowModel> {
	columns: GridCellParams<T>[] | GridColDef<T>[];
	dataSource: DataSource<T>;
	paginationModel?: PaginationModel;
	onPaginationChange?: (paginationModel: PaginationModel) => void;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	pageSizeOptions?: number[];
	disableRowSelectionOnClick?: boolean;
	localization?: Localization;
	children?: React.ReactNode;
	enableTreeData?: boolean;
	getTreeDataPath?: (row: T) => string[];
	getRowParentId?: (row: T) => string | number | null;
	treeDataMaxDepth?: number;
	showCountriesSearch?: boolean;
	members?: boolean;
	showCanceled?: boolean;
	startDateVacation?: string;
	startDateAbsence?: string;
	startDateAbsenceOverview?: string;
	endDateAbsenceOverview?: string;
	contactId?: string;
	showDropdownSearch?: boolean;
	showDeletedFilter?: boolean;
	greyDeletedRows?: boolean;
	redInvalidRows?: boolean;
	absence?: boolean;
	showOnlyChildren?: boolean;
	setEmployeeId?: (id: string | null) => void;
	rowSelectionModel?: GridRowSelectionModel;
	onRowSelectionModelChange?: (
		rowSelectionModel: GridRowSelectionModel,
		details: GridCallbackDetails,
	) => void;
	detailPanelContent?: (row?: T) => React.ReactNode;
	hasDetailPanelContent?: boolean;
	showSearch?: boolean;
	startYearDate?: string;
	endYearDate?: string;
	searchBy?: string;
	filterByProjectId?: string | null;
	filterByEmployeeId?: string | null;
	filterByStatuses?: ApprovalTimeEntryStatus[] | null;
	contactTypes?: ContactType[];
	customRowHeight?: {
		getRowHeight: (params: GridRowParams<T>) => number | "auto";
	};
	hideFooter?: boolean;
	docStatus?: DocumentStatus[];
	type?: DocumentType[];
	gridApiRef?: ReturnType<typeof useGridApiRef>;
}

const List = <T extends { id?: string | number }>({
	columns,
	dataSource,
	paginationModel: externalPaginationModel,
	onPaginationChange: externalPaginationChange,
	searchValue: externalSearchValue,
	onSearchChange: externalSearchChange,
	pageSizeOptions = [10, 25, 50, 100],
	disableRowSelectionOnClick = true,
	localization = {},
	children,
	enableTreeData = false,
	getTreeDataPath,
	getRowParentId,
	treeDataMaxDepth,
	showCountriesSearch = false,
	members = false,
	showCanceled = false,
	startDateVacation = dayjs().startOf("year").format("YYYY-MM-DD"),
	startDateAbsence = dayjs().startOf("year").format("YYYY-MM-DD"),
	startDateAbsenceOverview = dayjs().startOf("month").format("YYYY-MM-DD"),
	endDateAbsenceOverview = dayjs().endOf("month").format("YYYY-MM-DD"),
	contactId,
	showDropdownSearch = false,
	showDeletedFilter = false,
	greyDeletedRows = false,
	redInvalidRows = false,
	absence = false,
	showOnlyChildren = false,
	setEmployeeId = () => {},
	rowSelectionModel,
	onRowSelectionModelChange,
	detailPanelContent = () => <></>,
	hasDetailPanelContent = false,
	showSearch = true,
	startYearDate = dayjs().startOf("year").format("YYYY-MM-DD"),
	endYearDate = dayjs().endOf("year").format("YYYY-MM-DD"),
	searchBy = "",
	filterByProjectId,
	filterByEmployeeId,
	filterByStatuses,
	contactTypes = [],
	customRowHeight,
	hideFooter = false,
	type = [DocumentType.Invoice],
	gridApiRef: externalApiRef,
}: ListProps<T>) => {
	const { t, i18n } = useTranslation();

	const internalApiRef = useGridApiRef();
	const apiRef = externalApiRef ?? internalApiRef;

	const searchPlaceholder = localization.searchPlaceholder || t("placeholder.search");
	const noDataMessage = localization.noDataMessage || t("message.no-data");
	const [internalPaginationModel, setInternalPaginationModel] = useState<PaginationModel>(
		localStorage.getItem("paginationModel")
			? JSON.parse(localStorage.getItem("paginationModel") || "")
			: {
					page: 0,
					pageSize: 10,
				},
	);
	const [internalSearchValue, setInternalSearchValue] = useState("");

	const pageListName = useLocation().pathname.replace("/", "");

	const [sortModel, setSortModel] = React.useState<GridSortModel>(
		localStorage.getItem(pageListName + "sortModel")
			? JSON.parse(localStorage.getItem(pageListName + "sortModel") || "")
			: [],
	);
	const paginationModel = externalPaginationModel || internalPaginationModel;
	const searchValue = externalSearchValue ?? internalSearchValue;

	const showDeleted = useMemo(() => dataSource.showDeleted || false, [dataSource.showDeleted]);

	const [startDate, setStartDate] = useState<string>(dayjs().startOf("month").format("YYYY-MM-DD"));

	const [endDate, setEndDate] = useState<string>(dayjs().endOf("month").format("YYYY-MM-DD"));

	const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

	const [countries, setCountries] = useState<{ code: string; name: string }[]>([]);

	const [getCountries] = useGetAllFederalStatesLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data: { allFederalStates: { code: string; name: string }[] | null }) {
			if (data && data.allFederalStates) {
				setCountries(data.allFederalStates);
			}
		},
	});

	useEffect(() => {
		if (showCountriesSearch) {
			getCountries();
		}
	}, [showCountriesSearch]);

	const handlePaginationChange = (newPaginationModel: PaginationModel) => {
		if (externalPaginationChange) {
			externalPaginationChange(newPaginationModel);
		} else {
			setInternalPaginationModel(newPaginationModel);
		}
	};

	const handleSearchChange = (newSearchValue: string) => {
		if (externalSearchChange) {
			externalSearchChange(newSearchValue);
		} else {
			setInternalSearchValue(newSearchValue);
		}
	};

	useEffect(() => {
		if (dataSource.fetchData) {
			dataSource.fetchData(paginationModel, searchValue);
		}

		if (dataSource.fetchDataForContact) {
			dataSource.fetchDataForContact(paginationModel, searchValue, contactId ?? "");
		}

		if (dataSource.fetchDataForAbsence) {
			dataSource.fetchDataForAbsence(startDateAbsence, showCanceled, contactId, paginationModel);
		}
	}, [paginationModel, searchValue, contactId, showCanceled, startDateAbsence]);

	useEffect(() => {
		if (dataSource.fetchDataAcceptedVacations) {
			dataSource.fetchDataAcceptedVacations(paginationModel, startDateVacation);
		}
	}, [startDateVacation, paginationModel]);

	useEffect(() => {
		if (dataSource.fetchDeletedData) {
			dataSource.fetchDeletedData(paginationModel, searchValue, showDeleted);
		}
	}, [paginationModel, searchValue, showDeleted]);

	useEffect(() => {
		if (dataSource.fetchDataCommercialDocuments) {
			dataSource.fetchDataCommercialDocuments?.(
				paginationModel,
				dataSource.filter?.startDate || dayjs().startOf("year").format("YYYY-MM-DD"),
				dataSource.filter?.endDate || null,
				type,
				dataSource.filter.status,
				searchValue,
				dataSource.filter.isProforma,
				dataSource.filter.sent,
				dataSource.filter.projectId,
			);
		}
	}, [
		searchValue,
		paginationModel,
		dataSource.filter?.sent,
		dataSource.filter?.isProforma,
		dataSource.filter?.status,
		dataSource.filter?.startDate,
		dataSource.filter?.endDate,
		dataSource.filter?.projectId,
		type,
	]);
	useEffect(() => {
		localStorage.setItem("paginationModel", JSON.stringify(paginationModel));
	}, [paginationModel]);

	useEffect(() => {
		localStorage.setItem(pageListName + "sortModel", JSON.stringify(sortModel));
	}, [sortModel]);

	useEffect(() => {
		if (
			startDate &&
			endDate &&
			startDate !== "Invalid Date" &&
			endDate !== "Invalid Date" &&
			dayjs(endDate).isAfter(dayjs(startDate)) &&
			dataSource.fetchDataTimetracking
		) {
			dataSource.fetchDataTimetracking(paginationModel, startDate, endDate, selectedCountries);
		}
	}, [paginationModel, startDate, endDate, selectedCountries]);

	useEffect(() => {
		if (dataSource.fetchDataStartEnd) {
			dataSource.fetchDataStartEnd(
				paginationModel,
				startYearDate,
				endYearDate,
				searchBy,
				filterByProjectId,
				filterByEmployeeId,
				filterByStatuses,
			);
		}
	}, [
		startYearDate,
		endYearDate,
		paginationModel,
		searchBy,
		filterByProjectId,
		filterByEmployeeId,
		filterByStatuses,
	]);

	useEffect(() => {
		if (dataSource.fetchDataForAbsenceOverview) {
			dataSource.fetchDataForAbsenceOverview(
				startDateAbsenceOverview,
				endDateAbsenceOverview,
				paginationModel,
			);
		}
	}, [startDateAbsenceOverview, endDateAbsenceOverview, paginationModel]);

	useEffect(() => {
		if (dataSource.fetchDataWithType) {
			dataSource.fetchDataWithType(paginationModel, searchValue, contactTypes, showDeleted);
		}
	}, [paginationModel, searchValue, showDeleted]);

	const handleExpandClick = (params: GridRenderCellParams) => {
		if (params.row.type !== "group" || !apiRef.current) return;

		const rowNode = apiRef.current.getRowNode(params.row.id);
		const isExpanded = rowNode && "childrenExpanded" in rowNode ? rowNode.childrenExpanded : false;

		const hasChildren =
			rowNode && "children" in rowNode && rowNode.children && rowNode.children.length > 0;

		if (!isExpanded && !hasChildren) {
			dataSource.fetchSubData?.(params.row.id.toString()).then(() => {
				const rowN = apiRef.current?.getRowNode(params.row.id) ?? {};
				if ("children" in rowN && isArray(rowN.children) && rowN.children?.length > 0)
					apiRef.current?.setRowChildrenExpansion(params.row.id, true);
				dataSource.setIsLoading?.(false);
			});
		} else {
			apiRef.current?.setRowChildrenExpansion(params.row.id, !isExpanded);
			dataSource.setIsLoading?.(false);
		}
	};

	const [optionElement, setOptionElement] = usePersistentState("optionElement", {
		title: "",
		inputValue: "",
		item: null,
	});

	useEffect(() => {
		if (optionElement?.item) {
			if (optionElement.item.id) {
				dataSource.fetchDataForContact?.(paginationModel, searchValue, optionElement.item.id);
				setEmployeeId(optionElement.item.id);
			}
		} else {
			dataSource.fetchDataForContact?.(paginationModel, searchValue, "");
			setEmployeeId("");
		}
	}, [optionElement, paginationModel, searchValue]);

	useEffect(() => {
		if (dataSource.fetchProjectSummaryData) {
			dataSource.fetchProjectSummaryData();
		}
	}, [dataSource.filter]);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "auto" }}>
			<Box sx={{ flex: 1, height: "auto" }}>
				{!members && !absence && !showOnlyChildren ? (
					<div>
						{!showCountriesSearch ? (
							<Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
								{!showDropdownSearch ? (
									<>
										{showSearch ? (
											<TextField
												value={searchValue}
												onChange={(e) => handleSearchChange(e.target.value)}
												placeholder={searchPlaceholder}
												variant="outlined"
												sx={{ flex: 1 }}
											/>
										) : (
											<></>
										)}
									</>
								) : (
									<Box sx={{ flex: 1 }}>
										<Box className="w-60 left">
											<Dropdown
												doNotShowMenu={true}
												element={optionElement}
												setElement={(o) => {
													if (o?.item?.id !== optionElement?.item?.id) setOptionElement(o);
												}}
												elements={dataSource.contacts as Contact[]}
												searchPlaceholder={t("dropdown.placeholderEmployee")}
												searchLabel={t("dropdown.employeeSearch")}
												noData={t("dropdown.noEmployees")}
											/>
										</Box>
										<Box className="w-40 padding7 left">
											<Switch
												checked={!!dataSource.showDeleted}
												onChange={(e) => dataSource.setShowDeleted?.(e.target.checked)}
												color="primary"
												inputProps={{ "aria-label": "Show deleted" }}
											/>
											<label>{t("show-inactive")}</label>
										</Box>
									</Box>
								)}
								{showDeletedFilter && (
									<Box className="w-25 padding7 left">
										<Switch
											checked={!!dataSource.showDeleted}
											onChange={(e) => dataSource.setShowDeleted?.(e.target.checked)}
											color="primary"
											inputProps={{ "aria-label": "Show deleted" }}
										/>
										<label>{t("show-inactive")}</label>
									</Box>
								)}
								{children && <Box>{children}</Box>}
							</Box>
						) : (
							<Box className="w-100 fullCenter">
								<Box className="w-40 left">
									<Box className="flex-row gap-2" sx={{ width: "100%" }}>
										<Box sx={{ flex: 1 }}>
											<DateField
												isLoaded={true}
												value={startDate}
												setValue={setStartDate}
												label={t("timetracking.startDate")}
											/>
										</Box>
										<Box sx={{ flex: 1 }}>
											<DateField
												isLoaded={true}
												value={endDate}
												setValue={setEndDate}
												label={t("timetracking.endDate")}
											/>
										</Box>
									</Box>
									<Box className="w-100 left" style={{ height: 30 }}>
										<p style={{ marginTop: 15 }} className="red">
											{dayjs(endDate).isAfter(dayjs(startDate))
												? " "
												: t("timetracking.calendarEvents.errorDate")}
										</p>
									</Box>
								</Box>
								<Box className="w-40 left">
									<Autocomplete
										multiple
										disableCloseOnSelect
										options={countries}
										getOptionLabel={(option) => option.name}
										onChange={(_, value) => {
											if (value.length > 0) {
												const selected = value.map((v) => v.code);
												setSelectedCountries(selected);
											} else {
												setSelectedCountries([]);
											}
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t("timetracking.calendarEvents.selectCountries")}
												variant="outlined"
											/>
										)}
										renderOption={(
											props,
											option: { code: string; name: string },
											{ selected }: { selected: boolean },
										) => {
											const { ...optionProps } = props;
											return (
												<li {...optionProps} key={option.code}>
													<Checkbox
														icon={icon}
														checkedIcon={checkedIcon}
														style={{ marginRight: 8 }}
														checked={selected}
													/>
													{option.name}
												</li>
											);
										}}
									/>
								</Box>
								{children && <Box>{children}</Box>}
							</Box>
						)}
					</div>
				) : (
					<Box className="w-100">{children && <Box>{children}</Box>}</Box>
				)}

				<LoadingOverlay isLoading={dataSource.isLoading}>
					<Box sx={{ flexGrow: 1, clear: "both" }}>
						{!dataSource.isLoading && dataSource.rows.length === 0 ? (
							<NoData message={noDataMessage} />
						) : (
							<DataGridPremium
								localeText={
									i18n.language === "de"
										? dataGridDeDE.components.MuiDataGrid.defaultProps.localeText
										: {}
								}
								sx={{ border: "none", backgroundColor: "transparent" }}
								sortModel={sortModel}
								onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
								getRowId={(row: GridRowParams | T) => row.id as GridRowId}
								pagination
								apiRef={apiRef}
								rows={[...(dataSource.rows ?? []), ...(dataSource.subRows ?? [])]}
								columns={columns as GridCellParams<T>[]}
								rowCount={dataSource.rowCount}
								pageSizeOptions={pageSizeOptions}
								paginationMode="server"
								paginationModel={paginationModel}
								onPaginationModelChange={(newPage: PaginationModel) =>
									handlePaginationChange(newPage)
								}
								disableRowSelectionOnClick={disableRowSelectionOnClick}
								className="table"
								{...(customRowHeight
									? {
											getRowHeight: () => "auto",
										}
									: {})}
								{...(greyDeletedRows
									? {
											getCellClassName: (params: GridCellParams<TimeBalanceHistoryEntry>) => {
												if (params.row.isDeleted) {
													return "greyBackground opacity";
												}
											},
										}
									: {})}
								{...(redInvalidRows
									? {
											getCellClassName: (params: GridCellParams<CommercialDocumentSummary>) => {
												if (params.row.isInvalid) {
													return "redBackground";
												}
											},
										}
									: {})}
								{...(hasDetailPanelContent
									? {
											getDetailPanelHeight: () => "auto",
											getDetailPanelContent: (params: GridRowParams<T>) => {
												const row = params.row;
												if (
													("details" in row && isArray(row.details) && row.details?.length) ||
													("history" in row && isArray(row.history) && row.history?.length)
												) {
													return detailPanelContent(row as T);
												}
												return null;
											},
											slots: {
												detailPanelCollapseIcon: () => <ExpandLess />,
												detailPanelExpandIcon: () => <ExpandMore />,
											},
										}
									: {})}
								{...(enableTreeData && getTreeDataPath && getRowParentId
									? {
											getTreeDataPath,
											getRowParentId,
											treeData: true,
											groupingColDef: {
												headerName: t("name"),
												width: 300,
												renderCell: (params: GridCellParams) => {
													const rowNode = apiRef.current?.getRowNode(params.id);
													const isExpanded =
														rowNode && "childrenExpanded" in rowNode
															? rowNode.childrenExpanded
															: false;
													return (
														<div style={{ marginLeft: params.rowNode.depth * 50 }}>
															{params.row.isGroup && (
																<IconButton
																	style={{
																		marginLeft: params.rowNode.depth > 0 ? -35 : 0,
																	}}
																	size="small"
																	onClick={() => {
																		dataSource.setIsLoading?.(true);
																		handleExpandClick(params as GridRenderCellParams);
																	}}>
																	{isExpanded ? <ArrowDropDown /> : <ArrowRight />}
																</IconButton>
															)}
															{params.row.name}
														</div>
													);
												},
											},
											defaultGroupingExpansionDepth: treeDataMaxDepth ?? -1,
										}
									: {})}
								{...(rowSelectionModel && onRowSelectionModelChange
									? {
											rowSelectionModel: rowSelectionModel ?? [],
											onRowSelectionModelChange: (
												rowSelectionModel: GridRowSelectionModel,
												details: GridCallbackDetails,
											) => {
												onRowSelectionModelChange(rowSelectionModel, details);
											},
											checkboxSelection: true,
											disableSelectionOnClick: true,
											disableColumnMenu: true,
											disableColumnSelector: true,
											disableDensitySelector: true,
											disableVirtualization: true,
											componentsProps: {
												baseCheckbox: {
													color: "primary",
												},
											},
										}
									: {
											disableSelectionOnClick: true,
											disableColumnMenu: true,
											disableColumnSelector: true,
											disableDensitySelector: true,
											disableVirtualization: true,
										})}
								hideFooter={hideFooter}
							/>
						)}
					</Box>
				</LoadingOverlay>
			</Box>
		</Box>
	);
};

export default List;
